import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from './Dropdown';

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}

/** Build a list of page numbers to render, inserting ellipsis tokens (-1)
 *  when there's a gap. Always shows: 1, current-1, current, current+1, last.
 *  Adjacent gaps collapse to a single ellipsis. */
function pageRange(current: number, total: number): (number | -1)[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const out: (number | -1)[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push(-1);
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 1) out.push(-1);
  out.push(total);
  return out;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
}: PaginationProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pages = pageRange(page, totalPages);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
      <div
        className="flex items-center gap-2 text-[13px]"
        style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
      >
        <span>{t('pro.pagination.show', 'Affichage')}</span>
        <Dropdown<string>
          value={String(pageSize)}
          onChange={(v) => onPageSizeChange?.(Number(v))}
          variant="outline"
          size="sm"
          options={pageSizeOptions.map((opt) => ({
            key: String(opt),
            label: String(opt),
          }))}
        />
        <span>
          {t('pro.pagination.of', 'sur')} {total}
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        <button
          type="button"
          aria-label={t('pro.pagination.prev', 'Précédent')}
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-40"
          style={{
            border: '1px solid var(--pro-border)',
            color: 'var(--pro-text-dark)',
            background: 'white',
          }}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        {pages.map((p, i) =>
          p === -1 ? (
            <span
              key={`ellipsis-${i}`}
              className="w-8 h-8 flex items-center justify-center text-[13px]"
              style={{ color: 'var(--pro-text-grey)' }}
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className="w-8 h-8 rounded-lg text-[13px] font-medium flex items-center justify-center"
              style={
                p === page
                  ? { background: 'var(--pro-primary)', color: 'white' }
                  : {
                      border: '1px solid var(--pro-border)',
                      color: 'var(--pro-text-dark)',
                      background: 'white',
                    }
              }
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          aria-label={t('pro.pagination.next', 'Suivant')}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-40"
          style={{
            border: '1px solid var(--pro-border)',
            color: 'var(--pro-text-dark)',
            background: 'white',
          }}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
